import {
  SandboxMessageResult,
  SandboxReplyCause,
  SandboxToolTrace
} from "../aiAgents.types";

// Pure sandbox helpers (no React, no API client), so they can be unit tested.

// The notes are i18n keys (= the English text), translated by the page.
export const SANDBOX_TURN_NOTE = {
  escalated: "Assistant handed off to a human. Reason: {{reason}}",
  noReply: "The assistant produced no reply.",
  notSimulated:
    "No text from the assistant: a tool above delivers the message, and what the customer reads of it is not simulated here.",
  alsoNotSimulated:
    "A tool above also sent a message: what the customer reads of it is not simulated here."
} as const;

// Only "max-rounds" is the step limit. The two others say what the
// conversations page says of the same turns (aiTurn.reason.*).
export const SANDBOX_EXHAUSTED_NOTE: Partial<
  Record<SandboxReplyCause, string>
> = {
  "max-rounds": "The assistant produced no reply (reached the step limit).",
  "dropped-tool-calls":
    "The assistant produced no reply: it stopped before the tools it announced could run.",
  "annotation-only":
    "The assistant produced no reply: it wrote only internal notes."
};

// What the customer reads of the product sends of a turn, in the order they
// went out: the page's card, a single card's caption. A send the sandbox does
// not print (no `shown`, or `printed` ""), a refused call and a failed one
// give nothing.
export const sandboxPrintedTexts = (trace: SandboxToolTrace[]): string[] =>
  trace
    .map((entry) => entry.shown?.printed.trim() ?? "")
    .filter((text) => text !== "");

// The tools that put something in the chat, the backend's VISUAL_TOOLS
// (conversation-turn-decisions.util.ts) but show_more_products, which sends
// nothing in the sandbox: a page caches no list to continue.
const SANDBOX_SENDS = new Set([
  "send_products",
  "send_packs",
  "send_product_list",
  "send_ad_products",
  "send_product_video",
  "send_guide_video",
  "start_pack_builder"
]);

// Whether a send went out that the sandbox does not print: a pack, a video, a
// carousel, a Flow picker page (`printed` ""). Not a refused call nor a failed
// one: they sent nothing.
export const sandboxSentUnprinted = (trace: SandboxToolTrace[]): boolean =>
  trace.some(
    (entry) =>
      SANDBOX_SENDS.has(entry.toolName) &&
      !entry.refused &&
      !entry.isError &&
      !entry.shown?.printed.trim()
  );

export type SandboxTurnView =
  | { kind: "bubbles"; texts: string[]; note?: string }
  | { kind: "note"; key: string };

// What the assistant's side of a turn shows. Since 4 Nov 2026 the backend
// ends a turn that sent a selection page and no order tool as silent, the
// words dropped as production drops them, and this page said « step limit »
// on every one of them. What the customer reads there is the page's card, so
// that is the bubble, never the dropped words (the owner's simple version,
// 4 Nov 2026). One bubble per printed send, in the order they went out, as a
// page and a card are two WhatsApp messages (proposed 4 Nov 2026). Never
// « step limit » unless the turn hit it ("max-rounds").
export const sandboxTurnView = (
  result: Pick<SandboxMessageResult, "reply" | "toolTrace">
): SandboxTurnView => {
  const { reply } = result;
  switch (reply.kind) {
    case "reply":
      return reply.text
        ? { kind: "bubbles", texts: [reply.text] }
        : { kind: "note", key: SANDBOX_TURN_NOTE.noReply };
    case "escalate":
      return { kind: "note", key: SANDBOX_TURN_NOTE.escalated };
    case "silent": {
      // Every silent turn delivered a page or a visual: when the sandbox
      // prints none of it, the note says so rather than « no reply ». When it
      // prints some, a note under the bubbles says what else went out, so
      // they never read as the whole message (review, 4 Nov 2026).
      const texts = sandboxPrintedTexts(result.toolTrace);
      if (texts.length === 0) {
        return { kind: "note", key: SANDBOX_TURN_NOTE.notSimulated };
      }
      return sandboxSentUnprinted(result.toolTrace)
        ? { kind: "bubbles", texts, note: SANDBOX_TURN_NOTE.alsoNotSimulated }
        : { kind: "bubbles", texts };
    }
    default:
      // exhausted, or a kind this admin does not know yet.
      return {
        kind: "note",
        key:
          (reply.cause && SANDBOX_EXHAUSTED_NOTE[reply.cause]) ||
          SANDBOX_TURN_NOTE.noReply
      };
  }
};
